import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { RefreshCw } from 'lucide-react';
import { APP_VERSION, formatAppVersion } from '@/lib/app-version';

/**
 * Detects when a newer deployed build is available by comparing the version
 * baked into the currently running JS bundle with the no-cache server version.
 */

/** How often to check for a new build while the tab is active. */
const POLL_MS = 2 * 60 * 1000;

/**
 * Returns true in any context where we must NOT poll for new builds: dev,
 * inside an iframe (the Lovable editor preview), or on Lovable preview/dev
 * hostnames. The preview is a real content-hashed build that rebuilds on every
 * edit, so polling there fires a false "new version available" toast
 * constantly while you're working.
 */
function shouldSuppress(): boolean {
  if (!import.meta.env.PROD) return true;
  try {
    if (window.self !== window.top) return true; // inside an iframe (editor preview)
  } catch {
    return true; // cross-origin frame access threw -> we're framed
  }
  const host = window.location.hostname;
  if (
    host.startsWith('id-preview--') ||
    host.startsWith('preview--') ||
    host === 'lovableproject.com' ||
    host.endsWith('.lovableproject.com') ||
    host === 'lovableproject-dev.com' ||
    host.endsWith('.lovableproject-dev.com') ||
    host === 'beta.lovable.dev' ||
    host.endsWith('.beta.lovable.dev')
  ) {
    return true;
  }
  return false;
}

function normaliseVersion(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

async function fetchLatestVersion(): Promise<string | null> {
  try {
    const url = new URL('/api/public/version', window.location.origin);
    url.searchParams.set('_', Date.now().toString());
    const res = await fetch(url.toString(), {
      cache: 'no-store',
      headers: {
        accept: 'application/json',
        'cache-control': 'no-cache',
        pragma: 'no-cache',
      },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { version?: unknown };
    return normaliseVersion(data.version);
  } catch {
    return null;
  }
}

async function refreshToLatestBuild() {
  try {
    const registrations = await navigator.serviceWorker?.getRegistrations?.();
    await Promise.all(
      (registrations ?? []).map((registration) => registration.update().catch(() => undefined)),
    );
  } catch {
    // Updating push workers is best-effort; the navigation below is what loads the new app.
  }

  const nextUrl = new URL(window.location.href);
  nextUrl.searchParams.set('__app_update', Date.now().toString());
  window.location.replace(nextUrl.toString());
}

const TOAST_ID = 'app-update-available';

/**
 * localStorage key holding the last confirmed "newer version" we've already
 * prompted about. Persisting it means we don't re-nag for the same available
 * update across reloads / app restarts — only when an even newer build appears,
 * or when the update is finally installed (current === notified) do we reset.
 */
const NOTIFIED_VERSION_KEY = 'app-update-notified-version';

function readNotifiedVersion(): string | null {
  try {
    return normaliseVersion(window.localStorage.getItem(NOTIFIED_VERSION_KEY));
  } catch {
    return null;
  }
}

function writeNotifiedVersion(version: string): void {
  try {
    window.localStorage.setItem(NOTIFIED_VERSION_KEY, version);
  } catch {
    // Ignore (private mode / storage disabled) — falls back to in-memory only.
  }
}

function clearNotifiedVersion(): void {
  try {
    window.localStorage.removeItem(NOTIFIED_VERSION_KEY);
  } catch {
    // Ignore.
  }
}

/** Delay between the initial detection and the confirming re-fetch (ms). */
const CONFIRM_DELAY_MS = 1500;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

export default function UpdateAvailableToast() {
  const [mounted, setMounted] = useState(false);
  // The version baked into the bundle that is actually running in this tab.
  const currentVersion = useRef(normaliseVersion(APP_VERSION));
  // The newest server version we've already prompted about, so we re-prompt
  // only when an even newer build appears (instead of once per session). Seeded
  // from localStorage so the same available update doesn't re-nag across app
  // restarts until it's actually installed.
  const notifiedVersion = useRef<string | null>(readNotifiedVersion());
  // Guards against overlapping checks (e.g. focus + visibilitychange firing
  // together) while a silent confirmation pass is in flight.
  const checking = useRef(false);

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (!mounted) return;
    if (shouldSuppress()) return;

    let stopped = false;

    // The update was installed when the build now running matches the version we
    // last persisted a prompt for. Detect this immediately on startup (right
    // after the post-Update reload) and clear the stored key — no network
    // round-trip required, so the stale prompt never lingers.
    if (
      notifiedVersion.current &&
      currentVersion.current &&
      currentVersion.current !== 'dev' &&
      notifiedVersion.current === currentVersion.current
    ) {
      notifiedVersion.current = null;
      clearNotifiedVersion();
      toast.dismiss(TOAST_ID);
    }

    // Strip the cache-busting param we appended when triggering the Update so it
    // doesn't stick around in the address bar after the new build has loaded.
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.has('__app_update')) {
        url.searchParams.delete('__app_update');
        window.history.replaceState(window.history.state, '', url.toString());
      }
    } catch {
      // Best-effort URL tidy-up; ignore if History API is unavailable.
    }

    const showToast = (latestVersion: string) => {
      if (notifiedVersion.current === latestVersion) return;
      notifiedVersion.current = latestVersion;
      writeNotifiedVersion(latestVersion);
      toast('A new version is available', {
        id: TOAST_ID,
        description: currentVersion.current
          ? `Refresh to update from ${formatAppVersion(currentVersion.current)} to ${formatAppVersion(latestVersion)}.`
          : `Refresh to update to ${formatAppVersion(latestVersion)}.`,
        duration: Infinity,
        icon: <RefreshCw size={16} />,
        action: {
          label: 'Update',
          onClick: () => void refreshToLatestBuild(),
        },
      });
    };

    const isRealUpdate = (version: string | null): version is string =>
      !!version &&
      !!currentVersion.current &&
      currentVersion.current !== 'dev' &&
      version !== currentVersion.current;

    // Silent background check: fetches the latest deployed version on startup
    // and on resume. A mismatch is only surfaced as a toast AFTER a confirming
    // re-fetch agrees on the same newer version, so a transient/edge blip never
    // produces a user-visible prompt.
    const check = async () => {
      if (stopped || checking.current) return;
      checking.current = true;
      try {
        const first = await fetchLatestVersion();
        if (stopped) return;

        if (!isRealUpdate(first)) {
          if (first && first === currentVersion.current) {
            // Confirmed on the latest build — the pending update is now
            // installed, so clear the persisted prompt for good.
            notifiedVersion.current = null;
            clearNotifiedVersion();
            toast.dismiss(TOAST_ID);
          }
          return; // null/failed fetch: stay silent, try again later.
        }

        // Potential update detected — confirm it before showing anything.
        await delay(CONFIRM_DELAY_MS);
        if (stopped) return;
        const confirmed = await fetchLatestVersion();
        if (stopped) return;

        if (isRealUpdate(confirmed) && confirmed === first) {
          showToast(confirmed);
        }
      } finally {
        checking.current = false;
      }
    };

    // Background check on startup, then poll while the tab is active.
    check();
    const interval = window.setInterval(check, POLL_MS);
    // Resume triggers: tab becomes visible, window regains focus, or the page
    // is restored from the bfcache / reopened as an installed app.
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', check);
    window.addEventListener('pageshow', check);

    return () => {
      stopped = true;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', check);
      window.removeEventListener('pageshow', check);
    };
  }, [mounted]);

  return null;
}


